/**
 * Browser-based random data generators for testing and demonstration.
 * Uses Web Crypto API (crypto.getRandomValues) for high-quality randomness.
 */

export interface GeneratedData {
  data: Uint8Array | Float64Array
  bitLength?: number
  sampleCount: number
  description: string
  parameters: Record<string, number | string>
}

/**
 * Format a number with commas for human-readable descriptions.
 */
function formatNumber(n: number): string {
  return n.toLocaleString('en-US')
}

/**
 * Get an array of cryptographically random 32-bit unsigned integers.
 * Uses the Web Crypto API (crypto.getRandomValues).
 */
function cryptoRandomUint32(count: number): Uint32Array {
  const arr = new Uint32Array(count)
  // crypto.getRandomValues has a 65536-byte limit per call,
  // which is 16384 Uint32 values. Chunk if needed.
  const chunkSize = 16384
  for (let offset = 0; offset < count; offset += chunkSize) {
    const end = Math.min(offset + chunkSize, count)
    crypto.getRandomValues(arr.subarray(offset, end))
  }
  return arr
}

/**
 * Generate uniform random bits (P(1) = 0.5).
 * Returns packed Uint8Array (8 bits per byte, MSB first).
 */
export function generateUniformBits(numBits: number): GeneratedData {
  if (numBits <= 0) {
    return {
      data: new Uint8Array(0),
      bitLength: 0,
      sampleCount: 0,
      description: '0 uniform random bits (P(1) = 0.5)',
      parameters: { numBits: 0, p: 0.5 },
    }
  }

  const byteLength = Math.ceil(numBits / 8)
  const data = new Uint8Array(byteLength)
  // crypto.getRandomValues has a 65536-byte limit per call. Chunk if needed.
  const chunkSize = 65536
  for (let offset = 0; offset < byteLength; offset += chunkSize) {
    const end = Math.min(offset + chunkSize, byteLength)
    crypto.getRandomValues(data.subarray(offset, end))
  }

  // Mask off trailing bits in the last byte so they are zero
  const trailingBits = numBits % 8
  if (trailingBits !== 0) {
    const mask = 0xff << (8 - trailingBits)
    data[byteLength - 1] &= mask
  }

  return {
    data,
    bitLength: numBits,
    sampleCount: numBits,
    description: `${formatNumber(numBits)} uniform random bits (P(1) = 0.5)`,
    parameters: { numBits, p: 0.5 },
  }
}

/**
 * Generate Bernoulli(p) random bits with specified bias.
 * Each bit is 1 with probability p, 0 with probability (1-p).
 * Uses 32-bit crypto random values for precise threshold comparison.
 */
export function generateBernoulliBits(numBits: number, p: number): GeneratedData {
  if (numBits <= 0) {
    return {
      data: new Uint8Array(0),
      bitLength: 0,
      sampleCount: 0,
      description: `0 Bernoulli bits (P(1) = ${p})`,
      parameters: { numBits: 0, p },
    }
  }

  const byteLength = Math.ceil(numBits / 8)
  const result = new Uint8Array(byteLength)
  const threshold = Math.floor(p * 0xffffffff)
  const randomValues = cryptoRandomUint32(numBits)

  for (let i = 0; i < numBits; i++) {
    if (randomValues[i] < threshold) {
      result[i >>> 3] |= 0x80 >>> (i & 7)
    }
  }

  return {
    data: result,
    bitLength: numBits,
    sampleCount: numBits,
    description: `${formatNumber(numBits)} Bernoulli bits (P(1) = ${p})`,
    parameters: { numBits, p },
  }
}

/**
 * Generate Gaussian random samples using Box-Muller transform
 * with crypto random source.
 */
export function generateGaussianSamples(
  numSamples: number,
  mu: number,
  sigma: number,
): GeneratedData {
  if (numSamples <= 0) {
    return {
      data: new Float64Array(0),
      sampleCount: 0,
      description: `0 Gaussian samples (mu=${mu}, sigma=${sigma})`,
      parameters: { numSamples: 0, mu, sigma },
    }
  }

  const samples = new Float64Array(numSamples)
  // Box-Muller needs pairs of uniform samples. We need ceil(numSamples/2) pairs.
  const pairsNeeded = Math.ceil(numSamples / 2)
  const uniformCount = pairsNeeded * 2
  const randomValues = cryptoRandomUint32(uniformCount)

  for (let i = 0; i < pairsNeeded; i++) {
    // Convert to (0, 1) range, avoiding exact 0
    let u1 = (randomValues[i * 2] + 1) / (0xffffffff + 2)
    const u2 = randomValues[i * 2 + 1] / (0xffffffff + 1)

    // Clamp u1 away from 0 to avoid -Infinity from log
    if (u1 <= 0) u1 = Number.MIN_VALUE

    const r = Math.sqrt(-2.0 * Math.log(u1))
    const theta = 2.0 * Math.PI * u2

    const z0 = r * Math.cos(theta)
    const z1 = r * Math.sin(theta)

    samples[i * 2] = mu + sigma * z0
    if (i * 2 + 1 < numSamples) {
      samples[i * 2 + 1] = mu + sigma * z1
    }
  }

  return {
    data: samples,
    sampleCount: numSamples,
    description: `${formatNumber(numSamples)} Gaussian samples (mu=${mu}, sigma=${sigma})`,
    parameters: { numSamples, mu, sigma },
  }
}

/**
 * Generate samples from a first-order Markov chain.
 * p01 = P(X_t=1 | X_{t-1}=0), p10 = P(X_t=0 | X_{t-1}=1)
 * Useful for testing autocorrelation detection.
 */
export function generateMarkovChain(
  numBits: number,
  p01: number,
  p10: number,
): GeneratedData {
  if (numBits <= 0) {
    return {
      data: new Uint8Array(0),
      bitLength: 0,
      sampleCount: 0,
      description: `0 Markov chain bits (p01=${p01}, p10=${p10})`,
      parameters: { numBits: 0, p01, p10 },
    }
  }

  const byteLength = Math.ceil(numBits / 8)
  const result = new Uint8Array(byteLength)
  const randomValues = cryptoRandomUint32(numBits)

  const threshold01 = Math.floor(p01 * 0xffffffff)
  const threshold10 = Math.floor(p10 * 0xffffffff)

  // Stationary distribution: P(X=1) = p01 / (p01 + p10)
  const stationaryP1 = (p01 + p10 > 0) ? p01 / (p01 + p10) : 0.5
  const thresholdStart = Math.floor(stationaryP1 * 0xffffffff)

  // Initialize first bit from stationary distribution
  let currentBit = randomValues[0] < thresholdStart ? 1 : 0
  if (currentBit === 1) {
    result[0] |= 0x80
  }

  for (let i = 1; i < numBits; i++) {
    const r = randomValues[i]
    if (currentBit === 0) {
      // Transition 0 -> 1 with probability p01
      currentBit = r < threshold01 ? 1 : 0
    } else {
      // Transition 1 -> 0 with probability p10
      currentBit = r < threshold10 ? 0 : 1
    }
    if (currentBit === 1) {
      result[i >>> 3] |= 0x80 >>> (i & 7)
    }
  }

  return {
    data: result,
    bitLength: numBits,
    sampleCount: numBits,
    description: `${formatNumber(numBits)} Markov chain bits (p01=${p01}, p10=${p10})`,
    parameters: { numBits, p01, p10 },
  }
}

/**
 * Generate bits with linear drift in bias (for testing stability detection).
 * Bias goes from pStart to pEnd linearly over the sequence.
 */
export function generateDriftingBias(
  numBits: number,
  pStart: number,
  pEnd: number,
): GeneratedData {
  if (numBits <= 0) {
    return {
      data: new Uint8Array(0),
      bitLength: 0,
      sampleCount: 0,
      description: `0 drifting-bias bits (${pStart} -> ${pEnd})`,
      parameters: { numBits: 0, pStart, pEnd },
    }
  }

  const byteLength = Math.ceil(numBits / 8)
  const result = new Uint8Array(byteLength)
  const randomValues = cryptoRandomUint32(numBits)

  for (let i = 0; i < numBits; i++) {
    // Linear interpolation from pStart to pEnd
    const t = numBits > 1 ? i / (numBits - 1) : 0
    const p = pStart + (pEnd - pStart) * t
    const threshold = Math.floor(p * 0xffffffff)

    if (randomValues[i] < threshold) {
      result[i >>> 3] |= 0x80 >>> (i & 7)
    }
  }

  return {
    data: result,
    bitLength: numBits,
    sampleCount: numBits,
    description: `${formatNumber(numBits)} drifting-bias bits (${pStart} -> ${pEnd})`,
    parameters: { numBits, pStart, pEnd },
  }
}

/**
 * Generate uniform random samples on [0, 1) for testing non-Gaussian rejection.
 */
export function generateUniformSamples(numSamples: number): GeneratedData {
  if (numSamples <= 0) {
    return {
      data: new Float64Array(0),
      sampleCount: 0,
      description: '0 uniform samples',
      parameters: { numSamples: 0 },
    }
  }

  const samples = new Float64Array(numSamples)
  const randomValues = cryptoRandomUint32(numSamples)

  for (let i = 0; i < numSamples; i++) {
    samples[i] = randomValues[i] / (0xffffffff + 1)
  }

  return {
    data: samples,
    sampleCount: numSamples,
    description: `${formatNumber(numSamples)} uniform samples on [0, 1)`,
    parameters: { numSamples },
  }
}

/**
 * Count the proportion of 1-bits in packed data (MSB first).
 */
export function countOnes(data: Uint8Array, bitLength: number): number {
  let count = 0
  for (let i = 0; i < bitLength; i++) {
    if ((data[i >>> 3] >> (7 - (i & 7))) & 1) {
      count++
    }
  }
  return count
}
